import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import styled from 'styled-components';
import clsx from 'clsx';
import SidebarHeader from './SidebarHeader';
import Drawer from '@material-ui/core/Drawer';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText';
import Button from '@material-ui/core/Button';
import { makeStyles } from '@material-ui/core/styles';
import { Colors, Metrics, Images } from '../../themes';
import { useTranslation } from 'react-i18next';
import SvgIcon from '../../icons/SvgIcon';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import { NavLink, withRouter } from 'react-router-dom';
import { navItems as routesData } from '../../Routes';
import Global from '../../lib/Global';
import AuthActions from '../../redux/AuthRedux';
import DistributorsActions from '../../redux/DistributorsRedux';
import CustomersActions from '../../redux/CustomersRedux';
import { checkRole, checkPermission } from '../../lib/Utils';
import { availableSettings } from '../../config/AppConfig';

const useStyles = makeStyles(() => ({
  navigation: {
    marginBottom: 'auto',
  },
  button: {
    height: 48,
    paddingLeft: 24,
    borderLeft: `4px solid ${Colors.transparent}`,
    '&:hover span, &$selected span': {
      color: Colors.white,
      fontWeight: 700,
    },
    '&:hover, &$selected': {
      background: Colors.hoverGrey,
      color: Colors.white,
      borderLeft: `4px solid ${Colors.orange}`,
    },
  },
  logout: {
    height: 48,
    borderRadius: 0,
    justifyContent: 'space-between',
    width: '100%',
    background: Colors.hoverGrey,
    color: Colors.lightGrey,
    paddingLeft: 32,
    paddingRight: 25,
    fontWeight: 600,
    fontSize: '0.9rem',
    '&:hover span': {
      color: Colors.white,
      fontWeight: 700,
    },
    '&:hover': {
      background: Colors.logoutHoverGrey,
      color: Colors.white,
      borderLeft: `4px solid ${Colors.orange}`,
    },
  },
  selected: {},
  listItemText: {
    textTransform: 'uppercase',
    color: Colors.lightGrey,
    fontSize: '0.9rem',
    fontWeight: 600,
  },
  drawerPaperScrollable: {
    width: Metrics.drawerWidth + 10,
    '&::-webkit-scrollbar': {
      width: 0,
      background: 'transparent',
    },
  },
  icon: {
    fontSize: 30,
    color: Colors.lightGrey,
  },
  iconHighlighted: {
    color: Colors.orange,
  },
}));

const useHover = () => {
  const ref = useRef(null);
  const [hovered, setHovered] = useState(false);
  const enter = useCallback(() => {
    setHovered(true);
  }, []);
  const leave = useCallback(() => {
    setHovered(false);
  }, []);
  useEffect(() => {
    if (ref.current) {
      const constantRef = ref.current;
      constantRef.addEventListener('mouseenter', enter);
      constantRef.addEventListener('mouseleave', leave);
      return () => {
        if (constantRef) {
          constantRef.removeEventListener('mouseenter', enter);
          constantRef.removeEventListener('mouseleave', leave);
        }
      };
    }
  }, [ref, enter, leave]);
  return [ref, hovered];
};

const Container = styled.div`
  background: ${Colors.navBackgroundGrey};
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`;

const CustomNav = styled.nav`
  @media (min-width: ${(props) =>
      props.isControllers ? Metrics.breakpoints.lg + Metrics.controllersAddToLg : Metrics.breakpoints.lg}px) {
    width: ${Metrics.drawerWidth}px;
    flex-shrink: 0;
  }
`;

const LeftSidebar = (props) => {
  const currentRoute = routesData.find((route) => route.route === props.location.pathname);
  const isControllers = currentRoute?.route === '/controllers';
  const dispatch = useDispatch();
  const { container, handleDrawerToggle, mobileOpen } = props;
  const classes = useStyles();
  const { t } = useTranslation();
  const biggerThanLarge = useMediaQuery(
    `(min-width: ${Metrics.breakpoints.lg + (isControllers ? Metrics.controllersAddToLg : 0)}px)`,
  );
  const [ref0, hovered0] = useHover();
  const [ref1, hovered1] = useHover();
  const [ref2, hovered2] = useHover();
  const [ref3, hovered3] = useHover();
  const [ref4, hovered4] = useHover();
  const [ref5, hovered5] = useHover();
  const [ref6, hovered6] = useHover();
  const [ref7, hovered7] = useHover();
  const [ref8, hovered8] = useHover();
  const [ref9, hovered9] = useHover();
  const [refReportsButton, hoveredReportsButton] = useHover();
  const [refAdminButton, hoveredAdminButton] = useHover();
  const [refDashboardsButton, hoveredDashboardsButton] = useHover();
  const refs = [ref0, ref1, ref2, ref3, ref4, ref5, ref6, ref7, ref8, ref9];
  const hoveredAll = [
    hovered0,
    hovered1,
    hovered2,
    hovered3,
    hovered4,
    hovered5,
    hovered6,
    hovered7,
    hovered8,
    hovered9,
  ];
  const [isAdminMenuOpen, setAdminMenuOpen] = useState(false);
  const [isReportsMenuOpen, setReportsMenuOpen] = useState(false);
  const [isDashboardsMenuOpen, setDashboardsMenuOpen] = useState(false);
  const role = useSelector((state) => state.auth.role);
  const hasAjax = useSelector((state) => state.auth.hasAjax);
  const hasFakeSetting = useSelector((state) => state.auth.hasFakeSetting);
  const distributorId = useSelector((state) => state.auth.distributorId);
  const isSystemAdmin = checkRole(role, 'systemAdmin');
  const isDistributorAdmin = checkRole(role, 'distributorAdmin');
  const isCustomerAdmin = checkRole(role, 'customerAdmin');
  const isTicketingAdmin = checkRole(role, 'ticketingAdmin');
  const navItems = routesData
    .map((item, index) => ({
      ...item,
      name: t(item.name),
      ref: refs[index],
      hovered: hoveredAll[index],
    }))
    .filter(
      // show all items for all roles, except for reception user: show only routes that have the showToReceptionUser flag
      (routeItem) =>
        isSystemAdmin ||
        isDistributorAdmin ||
        isCustomerAdmin ||
        (isTicketingAdmin && !routeItem.hideToTicketingAdmin) ||
        routeItem.showToReceptionUser,
    );
  const customerIdSub = useSelector((state) => state.auth.customerIdSub);
  const distributorIdSub = useSelector((state) => state.auth.distributorIdSub);
  const defaultDistributorIdSub = useSelector((state) => state.auth.defaultDistributorIdSub);
  const isDefaultDistributor = defaultDistributorIdSub === distributorIdSub;
  const customers = useSelector((state) => state.customers.customers);
  const [showDashboard, setShowDashboard] = useState(false);

  useEffect(() => {
    dispatch(DistributorsActions.getDistributorInfoRequest(distributorId));
    if (!isDefaultDistributor) {
      isDistributorAdmin || isSystemAdmin
        ? dispatch(CustomersActions.getCustomersByDistributorRequest(distributorId))
        : dispatch(CustomersActions.customersRequest());
    }
    isSystemAdmin && dispatch(DistributorsActions.getDistributorsListRequest());
  }, [dispatch, distributorId, isSystemAdmin, isDefaultDistributor, isDistributorAdmin]);

  useEffect(() => {
    const showAjax =
      (isSystemAdmin || isDistributorAdmin || isCustomerAdmin || isTicketingAdmin) &&
      checkPermission(customers, customerIdSub, availableSettings.ajax);
    dispatch(AuthActions.setHasAjax(showAjax));
    const showFakeSetting = checkPermission(customers, customerIdSub, availableSettings.fakeSetting); // FakeSetting is just an example of another dashboard
    dispatch(AuthActions.setHasFakeSetting(showFakeSetting));
  }, [dispatch, customerIdSub, customers, isSystemAdmin, isDistributorAdmin, isCustomerAdmin, isTicketingAdmin]);

  useEffect(() => {
    setShowDashboard(hasAjax || hasFakeSetting);
  }, [hasAjax, hasFakeSetting]);

  const drawer = (
    <Container className="Sidebar__Container">
      <SidebarHeader
        handleDrawerToggle={handleDrawerToggle}
        biggerThanLarge={biggerThanLarge}
        isSystemAdmin={isSystemAdmin}
        isDistributorAdmin={isDistributorAdmin}
      />
      <List className={classes.navigation}>
        {/* Home */}
        {navItems
          .filter((route) => route.isFirst)
          .map((navItem) => (
            <ListItem
              component={NavLink}
              to={navItem.route}
              exact
              activeClassName={classes.selected}
              button
              className={classes.button}
              key={navItem.name}
              ref={navItem.ref}
              {...(!biggerThanLarge && { onClick: handleDrawerToggle })}
            >
              <ListItemIcon>
                <SvgIcon
                  width={32}
                  icon={navItem.icon}
                  color={
                    navItem.hovered || navItem.route === props.location.pathname ? Colors.orange : Colors.lightGrey
                  }
                />
              </ListItemIcon>
              <ListItemText
                className={classes.listItemText}
                classes={{ primary: classes.listItemText }}
                primary={navItem.name}
              />
            </ListItem>
          ))}
        {/* End of Home */}
        {/* Dashboards Toggle */}
        {showDashboard && (
          <ListItem
            button
            onClick={() => setDashboardsMenuOpen(!isDashboardsMenuOpen)}
            className={classes.button}
            key={'dashboards-toggle'}
            ref={refDashboardsButton}
          >
            <ListItemIcon>
              {isDashboardsMenuOpen ? (
                <ExpandLessIcon className={clsx([classes.icon, hoveredDashboardsButton && classes.iconHighlighted])} />
              ) : (
                <ExpandMoreIcon className={clsx([classes.icon, hoveredDashboardsButton && classes.iconHighlighted])} />
              )}
            </ListItemIcon>
            <ListItemText
              className={classes.listItemText}
              classes={{ primary: classes.listItemText }}
              primary={t('navigation.dashboards')}
            />
          </ListItem>
        )}
        {/* Dashboards Submenu: Ajax, etc. */}
        {isDashboardsMenuOpen &&
          navItems
            .filter((route) => route.isInDashboardsMenu)
            .filter(({ route }) => (route === '/ajax' && hasAjax) || (route === '/fakesetting' && hasFakeSetting)) // FakeSetting is just an example of another dashboard
            .map((navItem) => (
              <ListItem
                component={NavLink}
                to={navItem.route}
                exact
                activeClassName={classes.selected}
                button
                className={classes.button}
                key={navItem.name}
                ref={navItem.ref}
                {...(!biggerThanLarge && { onClick: handleDrawerToggle })}
              >
                <ListItemIcon>
                  {navItem.isPng ? (
                    <img
                      width={32}
                      height={32}
                      alt={navItem.icon}
                      src={
                        navItem.hovered || navItem.route === props.location.pathname
                          ? Images[navItem.icon]
                          : Images[`${navItem.icon}Off`]
                      }
                    />
                  ) : (
                    <SvgIcon
                      width={32}
                      icon={navItem.icon}
                      color={
                        navItem.hovered || navItem.route === props.location.pathname ? Colors.orange : Colors.lightGrey
                      }
                    />
                  )}
                </ListItemIcon>
                <ListItemText
                  className={classes.listItemText}
                  classes={{ primary: classes.listItemText }}
                  primary={navItem.name}
                />
              </ListItem>
            ))}
        {/* end of dashboards */}
        {navItems
          .filter(
            (route) =>
              !route.isFirst &&
              !route.isInDashboardsMenu &&
              !route.isInReportsMenu &&
              !route.isInAdminMenu &&
              !route.isInDistributorAdminMenu,
          )
          .map((navItem) => (
            <ListItem
              component={NavLink}
              to={navItem.route}
              exact
              activeClassName={classes.selected}
              button
              className={classes.button}
              key={navItem.name}
              ref={navItem.ref}
              {...(!biggerThanLarge && { onClick: handleDrawerToggle })}
            >
              <ListItemIcon>
                <SvgIcon
                  width={32}
                  icon={navItem.icon}
                  color={
                    navItem.hovered || navItem.route === props.location.pathname ? Colors.orange : Colors.lightGrey
                  }
                />
              </ListItemIcon>
              <ListItemText
                className={classes.listItemText}
                classes={{ primary: classes.listItemText }}
                primary={navItem.name}
              />
            </ListItem>
          ))}
        {/* Reports Toggle */}
        <ListItem
          button
          onClick={() => setReportsMenuOpen(!isReportsMenuOpen)}
          className={classes.button}
          key={'reports-toggle'}
          ref={refReportsButton}
        >
          <ListItemIcon>
            {isReportsMenuOpen ? (
              <ExpandLessIcon className={clsx([classes.icon, hoveredReportsButton && classes.iconHighlighted])} />
            ) : (
              <ExpandMoreIcon className={clsx([classes.icon, hoveredReportsButton && classes.iconHighlighted])} />
            )}
          </ListItemIcon>
          <ListItemText
            className={classes.listItemText}
            classes={{ primary: classes.listItemText }}
            primary={t('navigation.reports')}
          />
        </ListItem>
        {/* Reports submenu: Event reports, Monitoring, etc. */}
        {isReportsMenuOpen &&
          navItems
            .filter((route) => route.isInReportsMenu)
            .map((navItem) => (
              <ListItem
                component={NavLink}
                to={navItem.route}
                exact
                activeClassName={classes.selected}
                button
                className={classes.button}
                key={navItem.name}
                ref={navItem.ref}
                {...(!biggerThanLarge && { onClick: handleDrawerToggle })}
              >
                <ListItemIcon>
                  {navItem.isPng ? (
                    <img
                      width={32}
                      height={32}
                      alt={navItem.icon}
                      src={
                        navItem.hovered || navItem.route === props.location.pathname
                          ? Images[navItem.icon]
                          : Images[`${navItem.icon}Off`]
                      }
                    />
                  ) : (
                    <SvgIcon
                      width={32}
                      icon={navItem.icon}
                      color={
                        navItem.hovered || navItem.route === props.location.pathname ? Colors.orange : Colors.lightGrey
                      }
                    />
                  )}
                </ListItemIcon>
                <ListItemText
                  className={classes.listItemText}
                  classes={{ primary: classes.listItemText }}
                  primary={navItem.name}
                />
              </ListItem>
            ))}
        {/* Admin Toggle */}
        {(isSystemAdmin || isDistributorAdmin || isCustomerAdmin) && (
          <ListItem
            button
            onClick={() => setAdminMenuOpen(!isAdminMenuOpen)}
            className={classes.button}
            key={'admin-toggle'}
            ref={refAdminButton}
          >
            <ListItemIcon>
              {isAdminMenuOpen ? (
                <ExpandLessIcon className={clsx([classes.icon, hoveredAdminButton && classes.iconHighlighted])} />
              ) : (
                <ExpandMoreIcon className={clsx([classes.icon, hoveredAdminButton && classes.iconHighlighted])} />
              )}
            </ListItemIcon>
            <ListItemText
              className={classes.listItemText}
              classes={{ primary: classes.listItemText }}
              primary={t('navigation.admin')}
            />
          </ListItem>
        )}
        {/* Admin Submenu: Users, Controllers, Customers etc. */}
        {isAdminMenuOpen &&
          navItems
            .filter(
              (route) =>
                route.isInAdminMenu || ((isDistributorAdmin || isSystemAdmin) && route.isInDistributorAdminMenu),
            )
            .map((navItem) => (
              <ListItem
                component={NavLink}
                to={navItem.route}
                exact
                activeClassName={classes.selected}
                button
                className={classes.button}
                key={navItem.name}
                ref={navItem.ref}
                {...(!biggerThanLarge && { onClick: handleDrawerToggle })}
              >
                <ListItemIcon>
                  <SvgIcon
                    width={32}
                    icon={navItem.icon}
                    color={
                      navItem.hovered || navItem.route === props.location.pathname ? Colors.orange : Colors.lightGrey
                    }
                  />
                </ListItemIcon>
                <ListItemText
                  className={classes.listItemText}
                  classes={{ primary: classes.listItemText }}
                  primary={navItem.name}
                />
              </ListItem>
            ))}
        {/* end of admin */}
      </List>
      <Button
        className={classes.logout}
        ref={ref8}
        endIcon={<SvgIcon icon="Logout" color={hovered8 ? Colors.orange : Colors.lightGrey} />}
        onClick={() => {
          Global.api = {
            ...Global.api,
            accessToken: '',
          };
          !biggerThanLarge && handleDrawerToggle();
          dispatch(AuthActions.logout());
        }}
      >
        <p>{t('auth.logout')}</p>
      </Button>
    </Container>
  );

  return (
    <CustomNav isControllers={isControllers} aria-label="navigation">
      {biggerThanLarge ? (
        <Drawer
          classes={{
            paper: classes.drawerPaperScrollable,
          }}
          variant="permanent"
          open
        >
          {drawer}
        </Drawer>
      ) : (
        <Drawer
          container={container}
          variant="temporary"
          anchor={'left'}
          open={mobileOpen}
          onClose={handleDrawerToggle}
          classes={{
            paper: classes.drawerPaperScrollable,
          }}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile.
          }}
        >
          {drawer}
        </Drawer>
      )}
    </CustomNav>
  );
};

export default withRouter(LeftSidebar);
